import { Vector3 } from "@babylonjs/core";
import type { JungleObstacle } from "../environment/JungleGenerator";
import type { DifficultyId, DifficultyPreset } from "./Difficulty";
import { damageAggressionTuning } from "./GhostAggression";

export const GHOST_DISORIENTATION_SECONDS = 2;
export const GHOST_DISORIENTATION_COOLDOWN_SECONDS = 5;
export type DisorientationPhase = "IDLE" | "STAGGERED" | "PURSUE_LAST_KNOWN" | "LOOK_LEFT" | "LOOK_RIGHT" | "SEARCHING";

/** Timings only; each controller remains the sole writer of its navigation and pose. */
export class GhostDisorientation {
  private currentPhase: DisorientationPhase = "IDLE";
  private phaseElapsed = 0;
  private cooldownRemaining = 0;
  readonly lastKnownPlayerPosition = Vector3.Zero();
  private approachDirection = Vector3.Forward();

  get phase(): DisorientationPhase { return this.currentPhase; }
  get active(): boolean { return this.currentPhase !== "IDLE"; }
  get cooldown(): number { return this.cooldownRemaining; }
  get canActivate(): boolean { return !this.active && this.cooldownRemaining <= 0; }

  activate(playerPosition: Vector3, ghostPosition: Vector3): boolean {
    if (!this.canActivate) return false;
    this.lastKnownPlayerPosition.copyFrom(playerPosition);
    this.approachDirection.copyFrom(playerPosition.subtract(ghostPosition));
    this.approachDirection.y = 0;
    if (this.approachDirection.lengthSquared() < .0001) this.approachDirection.copyFromFloats(0, 0, 1);
    else this.approachDirection.normalize();
    this.currentPhase = "STAGGERED";
    this.phaseElapsed = 0;
    return true;
  }

  update(deltaSeconds: number, reachedLastKnown = false): DisorientationPhase {
    const delta = Math.max(0, deltaSeconds);
    if (this.currentPhase === "IDLE") {
      this.cooldownRemaining = Math.max(0, this.cooldownRemaining - delta);
      return this.currentPhase;
    }
    this.phaseElapsed += delta;
    if (this.currentPhase === "STAGGERED" && this.phaseElapsed >= GHOST_DISORIENTATION_SECONDS) {
      this.cooldownRemaining = GHOST_DISORIENTATION_COOLDOWN_SECONDS;
      this.transition("PURSUE_LAST_KNOWN");
    } else if (this.currentPhase !== "STAGGERED") {
      this.cooldownRemaining = Math.max(0, this.cooldownRemaining - delta);
      if (this.currentPhase === "PURSUE_LAST_KNOWN" && (reachedLastKnown || this.phaseElapsed >= 3)) this.transition("LOOK_LEFT");
      else if (this.currentPhase === "LOOK_LEFT" && this.phaseElapsed >= .55) this.transition("LOOK_RIGHT");
      else if (this.currentPhase === "LOOK_RIGHT" && this.phaseElapsed >= .55) this.transition("SEARCHING");
      else if (this.currentPhase === "SEARCHING" && this.phaseElapsed >= 1.15) this.transition("IDLE");
    }
    return this.currentPhase;
  }

  lookTarget(origin: Vector3): Vector3 {
    const side = this.currentPhase === "LOOK_LEFT" ? -1 : 1;
    const lateral = new Vector3(-this.approachDirection.z * side, 0, this.approachDirection.x * side);
    return origin.add(this.approachDirection.scale(.7)).add(lateral.scale(2.2));
  }

  reset(): void { this.currentPhase = "IDLE"; this.phaseElapsed = 0; this.cooldownRemaining = 0; }
  private transition(phase: DisorientationPhase): void { this.currentPhase = phase; this.phaseElapsed = 0; }
}

export function isFinalHpSurvivalDifficulty(difficulty: DifficultyPreset | DifficultyId): boolean {
  const id = typeof difficulty === "string" ? difficulty : difficulty.id;
  return id === "liar" || id === "rakus" || id === "buang_tebiat";
}

export function ghostHealthRevealSeconds(difficulty: DifficultyPreset | DifficultyId): number | undefined {
  const id = typeof difficulty === "string" ? difficulty : difficulty.id;
  return id === "liar" ? 2.5 : id === "rakus" ? 2 : id === "buang_tebiat" ? 1.5 : undefined;
}

export function pocongHpAfterFall(maximumHp: number, fall: number): number {
  if (fall >= 4) return 0;
  if (fall <= 0) return Math.max(1, Math.floor(maximumHp));
  if (fall === 3) return 1;
  const fraction = fall === 1 ? 2 / 3 : 1 / 3;
  return Math.max(1, Math.round(maximumHp * fraction));
}

export function hasJungleLineOfSight(from: Vector3, to: Vector3, obstacles: readonly JungleObstacle[], clearance = .12): boolean {
  const segment = to.subtract(from); segment.y = 0;
  const lengthSquared = segment.lengthSquared();
  if (lengthSquared < .0001) return true;
  return !obstacles.some((obstacle) => {
    const offset = obstacle.position.subtract(from); offset.y = 0;
    const t = Math.max(0, Math.min(1, Vector3.Dot(offset, segment) / lengthSquared));
    const nearest = from.add(segment.scale(t));
    return Vector3.DistanceSquared(nearest, obstacle.position) <= (obstacle.radius + clearance) ** 2;
  });
}

export function shouldInjuryRetreat(difficulty: DifficultyPreset, randomValue: number): boolean {
  const chance = damageAggressionTuning(difficulty)?.retreatChance ?? 0;
  return chance > 0 && Math.abs(randomValue - .5) < chance * .5;
}
